/**
 * Connectivity state machine for the Feathers / socket.io client.
 * See Internal Docs/websocket-stability-feature/IMPLEMENTATION_PLAN.md
 */

export const OFFLINE_GRACE_MS = 5000;
export const ERROR_VERIFY_DELAY_MS = 500;
export const STATES = Object.freeze({
  CONNECTING: 'connecting',
  ONLINE: 'online',
  RECONNECTING: 'reconnecting',
  OFFLINE: 'offline'
});

export function makeOfflineError() {
  const err = new Error('Timeout: Unable to establish connection. Device is offline.');
  err.name = 'Unavailable';
  err.code = 10902;
  err.className = 'User Offline';
  return err;
}

const noopDebug = { record() {} };

/**
 * @param {Object} deps
 * @param {Object} deps.socket
 * @param {Object} [deps.win]
 * @param {Object} [deps.wsDebug]
 * @param {Function} [deps.setTimeoutFn]
 * @param {Function} [deps.clearTimeoutFn]
 */
export function createConnectivity(deps) {
  const socket = deps && deps.socket;
  if (!socket || typeof socket.on !== 'function') {
    throw new Error('createConnectivity requires a socket with .on()');
  }

  const win = deps.win !== undefined
    ? deps.win
    : typeof window !== 'undefined' ? window : null;
  const wsDebug = deps.wsDebug || noopDebug;
  const setTimeoutFn = deps.setTimeoutFn || setTimeout;
  const clearTimeoutFn = deps.clearTimeoutFn || clearTimeout;

  let state = socket.connected ? STATES.ONLINE : STATES.CONNECTING;
  let graceTimer = null;
  let errorTimer = null;
  const waiters = [];
  const listeners = [];

  const clearGraceTimer = () => {
    if (graceTimer != null) {
      clearTimeoutFn(graceTimer);
      graceTimer = null;
    }
  };

  const startGraceTimer = () => {
    if (graceTimer != null) return;
    graceTimer = setTimeoutFn(() => {
      graceTimer = null;
      if (!socket.connected) transition(STATES.OFFLINE, 'grace_expired');
    }, OFFLINE_GRACE_MS);
  };

  const resolveWaiters = () => {
    const pending = waiters.splice(0, waiters.length);
    pending.forEach(({ resolve }) => {
      try { resolve(); } catch (e) { /* noop */ }
    });
  };

  const rejectWaiters = (error) => {
    const pending = waiters.splice(0, waiters.length);
    pending.forEach(({ reject }) => {
      try { reject(error); } catch (e) { /* noop */ }
    });
  };

  const transition = (to, reason) => {
    if (to === state) return;
    const from = state;

    if (to === STATES.ONLINE) {
      clearGraceTimer();
      resolveWaiters();
    } else if (to === STATES.OFFLINE) {
      clearGraceTimer();
      rejectWaiters(makeOfflineError());
    } else if (to === STATES.RECONNECTING) {
      startGraceTimer();
    }

    state = to;

    try { wsDebug.record('state', { from, to, reason }); } catch (e) { /* noop */ }

    const evt = { from, to, reason, at: Date.now() };
    listeners.slice().forEach((fn) => {
      try { fn(evt); } catch (e) { /* noop */ }
    });
  };

  const onDrop = (reason) => {
    if (socket.connected === true) return;
    if (state === STATES.ONLINE) {
      transition(STATES.RECONNECTING, reason);
      return;
    }
    if (state === STATES.CONNECTING || state === STATES.RECONNECTING) {
      startGraceTimer();
      return;
    }
  };

  const verifyAfterError = () => {
    errorTimer = null;
    if (state === STATES.ONLINE && socket.connected === false) {
      onDrop('error_verified');
    }
  };

  socket.on('connect', () => transition(STATES.ONLINE, 'connect'));
  socket.on('reconnect', () => transition(STATES.ONLINE, 'reconnect'));
  socket.on('disconnect', () => onDrop('disconnect'));
  socket.on('close', () => onDrop('close'));
  socket.on('reconnecting', () => onDrop('reconnecting'));
  socket.on('reconnect_attempt', () => onDrop('reconnect_attempt'));
  socket.on('connect_error', () => onDrop('connect_error'));
  socket.on('reconnect_error', () => onDrop('reconnect_error'));
  socket.on('connect_failed', () => onDrop('connect_failed'));
  socket.on('reconnect_failed', () => transition(STATES.OFFLINE, 'reconnect_failed'));
  socket.on('error', () => {
    if (errorTimer != null) {
      clearTimeoutFn(errorTimer);
    }
    errorTimer = setTimeoutFn(verifyAfterError, ERROR_VERIFY_DELAY_MS);
  });

  const onWindowOffline = () => {
    if (!socket.connected) transition(STATES.OFFLINE, 'navigator_offline');
  };
  const onWindowOnline = () => {
    if (state === STATES.OFFLINE && socket.connected) {
      transition(STATES.ONLINE, 'navigator_online');
    }
  };

  if (win && typeof win.addEventListener === 'function') {
    win.addEventListener('offline', onWindowOffline);
    win.addEventListener('online', onWindowOnline);
  }

  if (state === STATES.CONNECTING) {
    startGraceTimer();
  }

  return {
    getState() {
      return state;
    },
    isConnected() {
      return !!socket.connected;
    },
    waitForOnline() {
      // socket.io v2 fires 'reconnect' before socket.connected is true; emits sent
      // in that window are buffered and flushed on 'connect'.
      if (socket.connected || state === STATES.ONLINE) return Promise.resolve();
      if (state === STATES.OFFLINE) return Promise.reject(makeOfflineError());
      return new Promise((resolve, reject) => {
        waiters.push({ resolve, reject });
      });
    },
    onTransition(fn) {
      if (typeof fn !== 'function') return () => {};
      listeners.push(fn);
      return () => {
        const ix = listeners.indexOf(fn);
        if (ix !== -1) listeners.splice(ix, 1);
      };
    },
    destroy() {
      clearGraceTimer();
      if (errorTimer != null) {
        clearTimeoutFn(errorTimer);
        errorTimer = null;
      }
      waiters.length = 0;
      listeners.length = 0;
    }
  };
}

export default createConnectivity;
